import { Box, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { Provider } from "../../types/connecteeSubmission";
import { getProviderDisplayName, getProviderFirstName } from "../../utils/providerDisplay";
import { ProviderCard } from "./ProviderCard";

const TEAL = "#008080";
const MINT_BG = "#F4F9F8";

interface MatchFoundViewProps {
  provider: Provider;
}

export function MatchFoundView({ provider }: MatchFoundViewProps) {
  const name = getProviderDisplayName(provider);
  const firstName = getProviderFirstName(provider);

  const steps = [
    {
      title: "We found a match!",
      description: `Based on your submission, we have connected you with ${name} and shared your information with them.`,
      done: true,
      icon: <CheckCircleIcon sx={{ fontSize: 20, color: "#fff" }} />,
    },
    {
      title: "Send a message",
      description: "Introduce yourself and share a brief overview of what you are looking for.",
      done: false,
      icon: <SendOutlinedIcon sx={{ fontSize: 18, color: TEAL }} />,
    },
    {
      title: "Schedule consultation",
      description: `Coordinate directly with ${firstName} to decide on a call or meeting to see if it's a good fit.`,
      done: false,
      icon: <EventAvailableOutlinedIcon sx={{ fontSize: 18, color: TEAL }} />,
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: MINT_BG,
        width: "100%",
        minHeight: "100%",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={4} alignItems="center" sx={{ maxWidth: 1100, mx: "auto", width: "100%" }}>
        <Stack spacing={1.5} alignItems="center" textAlign="center" px={1}>
          <Typography variant="h4" fontWeight={700} color="#212121">
            We've Found a Great Unyte Provider for You!
          </Typography>
          <Typography variant="body1" color="#616161" maxWidth={720}>
            Thank you for taking this step toward your well-being! Based on the details you shared,
            we have found a certified Unyte Provider whose profile aligns with your criteria.
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="stretch"
          sx={{ width: "100%" }}
        >
          <Box flex={1} sx={{ display: "flex", "& > *": { flex: 1, width: "100%" } }}>
            <ProviderCard provider={provider} layout="stacked" />
          </Box>

          <Box
            flex={1}
            sx={{
              bgcolor: "#fff",
              border: "1px solid #E0E0E0",
              borderRadius: 2,
              p: 3,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" fontWeight={700} color="#212121" mb={3}>
              Your Path Forward
            </Typography>

            <Stack spacing={0} position="relative">
              {steps.map((step, index) => (
                <Stack
                  key={step.title}
                  direction="row"
                  spacing={2}
                  pb={index < steps.length - 1 ? 3 : 0}
                >
                  <Stack alignItems="center" sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        bgcolor: step.done ? TEAL : "#fff",
                        border: `2px solid ${TEAL}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                      }}
                    >
                      {step.icon}
                    </Box>
                    {index < steps.length - 1 && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 36,
                          bottom: -12,
                          width: 0,
                          borderLeft: "2px dotted #B0BEC5",
                        }}
                      />
                    )}
                  </Stack>
                  <Box pt={0.5}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        textTransform: "uppercase",
                        color: step.done ? TEAL : "#212121",
                        letterSpacing: 0.4,
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="#616161" mt={0.5}>
                      {step.description}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" pt={1}>
          <VerifiedOutlinedIcon sx={{ fontSize: 20, color: "#9E9E9E" }} />
          <Typography
            variant="caption"
            sx={{ color: "#9E9E9E", letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}
          >
            Certified Unyte Providers
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
